/**
 * Status feed events — the append-only log of apartment-status facts.
 *
 * PORTED from `platform-workflows/src/core/unit-status/events.ts`. Keep in sync;
 * the intended long-term home is `@urbanmix-tech/shared-js` so the workflow
 * server and the web app fold the same vocabulary. The server-only
 * `emitStatusEvent()` helper is deliberately not ported — the app reads these
 * events, it never writes them.
 *
 * The unit's *status* is not stored as a single mutable field (which can't
 * represent overlapping situations — occupied + moving out + new tenant
 * incoming). Instead each status change is emitted as a `state_*` feed event,
 * and `deriveUnitStatus()` folds the feed into the current view.
 *
 * The `state_` prefix keeps these distinct from the spinner/stop UI feed noise.
 */
export const StatusEvent = {
  /** Renewal in progress — current tenant is negotiating terms. */
  RenewalNegotiating: 'state_renewal_negotiating',
  /** Renewal terms locked (PM marked, or countersign notice sent). */
  RenewalTermsAgreed: 'state_renewal_terms_agreed',
  /** Current tenant gave notice / move-out scheduled (carries the date). */
  MoveoutScheduled: 'state_moveout_scheduled',
  /** Current tenant physically left → unit vacant. */
  TenantMovedOut: 'state_tenant_moved_out',
  /** Unit listed for re-lease. */
  ListingPublished: 'state_listing_published',
  /** A showing took place. */
  UnitShown: 'state_unit_shown',
  /** A new lease was executed (carries leaseType + incoming tenant). */
  LeaseSigned: 'state_lease_signed',
  /** Incoming tenant move-in scheduled (carries the date). */
  MoveinScheduled: 'state_movein_scheduled',
  /** Incoming tenant physically moved in → occupied. */
  TenantMovedIn: 'state_tenant_moved_in',
} as const;

export type StatusEventType = (typeof StatusEvent)[keyof typeof StatusEvent];

/** All status event types. */
export const STATUS_EVENT_TYPES: StatusEventType[] = Object.values(StatusEvent);

/** The two events that authoritatively flip physical occupancy. */
export const OCCUPANCY_EVENT_TYPES: StatusEventType[] = [
  StatusEvent.TenantMovedIn,
  StatusEvent.TenantMovedOut,
];

/** True when a feed event type is one of our status facts. */
export function isStatusEvent(eventType: string): boolean {
  return typeof eventType === 'string' && eventType.startsWith('state_');
}

/**
 * Native platform feed events (NOT emitted by the workflow server) that carry
 * the lease lifecycle. The fold consumes these so an incoming lease shows up the
 * moment it's drafted or sent, rather than only once fully signed.
 *
 * They carry no lease type, so renewal-vs-new is resolved from the originating
 * process's type — see `processTypeById` in `deriveUnitStatus`.
 */
export const NativeLeaseEvent = {
  LeaseCreated: 'lease_created',
  LeaseSent: 'lease_sent',
  LeaseResent: 'lease_resent',
  DocumentCompleted: 'document.completed',
  LeaseExecuted: 'lease_executed',
} as const;

export type NativeLeaseEventType = (typeof NativeLeaseEvent)[keyof typeof NativeLeaseEvent];

export const NATIVE_LEASE_EVENT_TYPES: NativeLeaseEventType[] = Object.values(NativeLeaseEvent);

/** True when a feed event type is one the fold understands. */
export function isRecognizedEvent(eventType: string): boolean {
  return (
    isStatusEvent(eventType) ||
    NATIVE_LEASE_EVENT_TYPES.includes(eventType as NativeLeaseEventType)
  );
}

/**
 * Every event type the fold reads — our `state_*` facts plus the native lease
 * lifecycle.
 *
 * Used to scope the Firestore feed query (`eventType in [...]`) so we pull the
 * events that matter rather than a project's entire feed (tens of thousands of
 * docs). Firestore caps `in` at 30 values; this list is 14. The upstream guidance
 * is "pass the whole feed, the fold ignores what it doesn't recognize" — filtering
 * to exactly the recognized set is equivalent input for far less I/O, but it does
 * mean this list must grow whenever the fold learns a new event type.
 */
export const RECOGNIZED_EVENT_TYPES: string[] = [
  ...STATUS_EVENT_TYPES,
  ...NATIVE_LEASE_EVENT_TYPES,
];

export interface StatusEventData {
  /** Process that produced the event (for correlation / dedupe). */
  processId?: string;
  /** Relevant date (scheduled or actual) as an ISO string. */
  date?: string;
  /** Tenant this event is about, when known. */
  tenantName?: string;
  /** "new_lease" | "renewal" — set on `state_lease_signed`. */
  leaseType?: string;
  [key: string]: unknown;
}
